import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";
import { KokeileTanaan, MiksiToimii, ValmisRatkaisu } from "@/components/hub/Callouts";

const SLUG = "oppimistorni-turvallisuus";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Oppimistornin turvallisuus – mitä vanhemman kannattaa tarkistaa? | Montessori24";
const DESC =
  "Mitä oppimistornin turvallisuudessa kannattaa huomioida? Tarkista vakaus, ikäsuositus, valmistajan ohjeet, sijoituspaikka ja tuotekohtaiset turvallisuustiedot.";
const PUBLISHED = "2026-07-04";
const HERO = articleHeroFor(SLUG);
const UPDATED = "2026-08-16";

export const Route = createFileRoute("/montessori-koulu/oppimistorni-turvallisuus")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      updated: UPDATED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      eyebrow="Turvallisuus"
      title="Oppimistornin turvallisuus – mitä vanhemman kannattaa tarkistaa?"
      lead="Oppimistorni voi helpottaa lapsen osallistumista esimerkiksi ruoanlaittoon ja muihin kodin askareisiin. Turvallinen käyttö riippuu kuitenkin tuotteen rakenteesta, lapsen valmiuksista, sijoituspaikasta ja valmistajan ohjeiden noudattamisesta."
      readingMinutes={6}
      publishedISO={PUBLISHED}
      updatedISO={UPDATED}
    >
      <MiksiToimii>
        <p>
          <strong>Tärkeää:</strong> Tarkista aina kyseisen oppimistornin valmistajan ikäsuositus, enimmäiskuormitus,
          käyttöohjeet ja turvallisuusvaroitukset. Tämä artikkeli on yleisluontoinen — se ei korvaa tuotekohtaisia ohjeita.
        </p>
      </MiksiToimii>

      <h2>Turvallisuusvaatimukset riippuvat tuotteesta</h2>
      <p>
        Oppimistornin valmistaja vastaa siitä, että tuote täyttää sitä koskevat soveltuvat turvallisuusvaatimukset.
        Vaatimukset voivat riippua tuotteen rakenteesta, käyttötarkoituksesta ja siitä, miten tuote on luokiteltu.
        Tarkista aina tuotekohtaiset turvallisuustiedot ja valmistajan käyttöohjeet.
      </p>
      <p>
        Emme siksi esitä tässä yleispäteviä mittoja, kaatumistestejä tai standardivaatimuksia, jotka pätisivät kaikkiin
        oppimistorneihin. Jos jollakin tuotteella on vahvistettuja vaatimustenmukaisuustietoja, ne näytetään kyseisen
        tuotteen omalla tuotesivulla — yhden tuotteen dokumentaatiota ei voi yleistää muihin malleihin.
      </p>

      <h2>1. Tarkista tuotekohtainen ikäsuositus</h2>
      <p>
        Ikäsuositukset vaihtelevat valmistajan ja mallin mukaan: korkeus, askelmien säätövara ja suojarakenteet eroavat
        toisistaan. Katso ikäsuositus aina kyseisen tuotteen tiedoista.
      </p>
      <ul>
        <li>Ikä yksin ei kerro valmiudesta — arvioi myös lapsen tasapainoa ja liikkumistaitoja.</li>
        <li>Lapsen tulisi pystyä käyttämään tuotetta valmistajan tarkoittamalla tavalla.</li>
        <li>Aikuisen läsnäolo on tärkeää myös silloin, kun lapsi on käyttänyt tornia jo pitkään.</li>
      </ul>

      <h2>2. Tarkista vakaus ennen käyttöä</h2>
      <p>
        Oppimistornin tulisi seistä vakaasti tasaisella alustalla eikä sen pitäisi heilua tai liikkua selvästi
        normaalissa käytössä.
      </p>
      <ul>
        <li>Kaikki kiinnikkeet on kiristetty valmistajan ohjeen mukaan.</li>
        <li>Rakenne ei keiku eikä natise huolestuttavasti, kun tornia kokeillaan käsin.</li>
        <li>Lattia on tasainen ja kuiva.</li>
        <li>Tornia ei sijoiteta matolle tai epävakaalle alustalle, ellei valmistaja sitä salli.</li>
        <li>Mahdolliset liukuesteet ovat ehjät ja paikoillaan.</li>
      </ul>

      <h2>3. Valitse turvallinen sijoituspaikka</h2>
      <p>Suomalaisessa keittiössä työtaso on usein lähellä liettä ja ovea — mieti, mihin torni oikeasti mahtuu.</p>
      <ul>
        <li>Pidä torni kaukana portaista ja tasoeroista.</li>
        <li>Älä sijoita sitä ikkunan, parvekkeen oven tai verhonarujen viereen.</li>
        <li>Älä aseta tornia kuumien keittotasojen viereen ilman riittävää etäisyyttä ja valvontaa — hyvä paikka on esimerkiksi tiskipöydän tai ruokapöydän pää.</li>
        <li>Siirrä veitset, kuumat kattilat, pienet esineet ja pesuaineet lapsen ulottumattomiin.</li>
        <li>Varmista, ettei lapsi pääse tornista kiipeämään työtasolle, jääkaapin päälle tai muille korkeille kalusteille.</li>
      </ul>

      <h2>4. Tarkista rakenne</h2>
      <p>Katso tuotetta kokonaisuutena ja mieti, miten lapsi sitä oikeasti käyttää:</p>
      <ul>
        <li>Suojakaiteet tai muu suojarakenne ovat ehjät, jos tuotteessa sellaiset ovat.</li>
        <li>Mikään osa ei ole rikki tai löysällä.</li>
        <li>Reunat ovat viimeisteltyjä eikä teräviä kohtia ole.</li>
        <li>Kiinnikkeet eivät jää esiin häiritsevästi.</li>
        <li>Ilmeisiä puristumis- tai sormenjäämiskohtia ei ole.</li>
        <li>Askelmat ovat ehjät eivätkä liukkaat.</li>
        <li>Seisomataso tuntuu tukevalta.</li>
      </ul>
      <p>Nämä ovat havainnointiin perustuvia ohjeita, eivät mittavaatimuksia — tarkat mitat ja rakenteelliset vaatimukset määrittelee valmistaja.</p>

      <h2>5. Noudata valmistajan käyttöohjeita</h2>
      <p>Käy ohje läpi ennen ensimmäistä käyttökertaa ja tarkista ainakin nämä:</p>
      <ul>
        <li>ikäsuositus</li>
        <li>enimmäiskuormitus</li>
        <li>sallittu käyttäjämäärä</li>
        <li>sisä- ja ulkokäyttö</li>
        <li>taitto- ja lukitusmekanismit</li>
        <li>oikea kokoonpano ja sallitut säätöasennot</li>
        <li>asennusvaatimukset ja jälkikiristys</li>
      </ul>
      <p>Montessori24:n tuotesivuilla ilmoitettujen turvallisuustietojen tulee perustua valmistajan vahvistamiin tuotetietoihin.</p>

      <h2>6. Aikuisen valvonta</h2>
      <p>
        Oppimistorni ei korvaa aikuisen valvontaa. Erityisesti keittiössä lapsi voi päästä aiempaa korkeammalle ja
        lähemmäs kuumia pintoja, teräviä esineitä tai muita vaaroja. Sopikaa yhdessä selkeät säännöt siitä, milloin
        torniin noustaan ja mitä siinä tehdään.
      </p>

      <h2>7. Tarkista tuote säännöllisesti</h2>
      <ul>
        <li>ruuvit ja muut kiinnikkeet</li>
        <li>liitokset</li>
        <li>lukitusmekanismit</li>
        <li>puun vauriot ja halkeamat</li>
        <li>irtoavat osat</li>
        <li>kuluneet liukuesteet</li>
      </ul>
      <p>
        Jos rakenne on vaurioitunut tai epävakaa, lopeta käyttö, kunnes tuote on tarkistettu tai korjattu valmistajan
        ohjeiden mukaisesti.
      </p>

      <KokeileTanaan>
        <p>
          Tarkista oppimistornin kaikki näkyvät kiinnitykset ja varmista, ettei torni heilu käytössä. Katso samalla
          valmistajan ilmoittama ikäsuositus ja enimmäiskuormitus.
        </p>
      </KokeileTanaan>

      <h2>Mitä tuotetiedoista kannattaa etsiä?</h2>
      <ul>
        <li>valmistaja</li>
        <li>ikäsuositus</li>
        <li>enimmäiskuormitus</li>
        <li>tuotteen mitat</li>
        <li>käyttöohje</li>
        <li>varoitukset</li>
        <li>valmistajan turvallisuustiedot</li>
        <li>tuotekohtainen vaatimustenmukaisuustieto, jos sovellettavissa</li>
      </ul>

      <h2>Lähteet ja lisätietoa</h2>
      <ul>
        <li>Tuotteen valmistajan käyttöohje ja turvallisuustiedot — ensisijainen lähde tuotekohtaisissa kysymyksissä.</li>
        <li>Turvallisuus- ja kemikaalivirasto (Tukes): kuluttajaturvallisuutta ja lasten tuotteita koskeva ohjeistus.</li>
        <li>Kilpailu- ja kuluttajavirasto (KKV): kuluttajan oikeudet ja tuotetiedot.</li>
        <li>Euroopan unioni: yleinen tuoteturvallisuusasetus (EU) 2023/988 sekä leluturvallisuusdirektiivi 2009/48/EY — sovellettavuus riippuu tuotteen luokittelusta.</li>
      </ul>

      <h2>Lue myös</h2>
      <p>
        Käytännön ideoita keittiöön löydät artikkelista{" "}
        <Link to="/montessori-koulu/lapsi-keittiossa-ikaopas">Lapsi keittiössä — ikäopas</Link> ja itsenäisyyden
        tukemiseen artikkelista{" "}
        <Link to="/montessori-koulu/pienet-muutokset-kotona-itsenaisyys">Pienet muutokset kotona</Link>.
      </p>

      <ValmisRatkaisu
        intro="Vertaile malleja rauhassa ja lue kunkin tuotteen omat turvallisuustiedot ja valmistajan ohjeet ennen ostoa."
        picks={[
          { to: "/oppimistorni", label: "Oppimistornit", note: "Kaikki mallit ja tuotekohtaiset tiedot." },
          { to: "/vertailu", label: "Tuotevertailu", note: "Mitat ja ominaisuudet rinnakkain." },
        ]}
      />
    </BlogPost>
  );
}
